import { useQuery } from '@tanstack/react-query'
import type { ActivityCatalogEntry } from '../types'
import * as api from '../api/client'
import KPIChart, { KPI_DEFS } from './KPIChart'
import DecisionHistoryList from './DecisionHistoryList'

/**
 * Small multiples of this team's visible KPIs over time. Hidden stocks
 * (Resistance, Change Capacity, etc.) are not shown here — they remain
 * a surprise until the facilitator's debrief view reveals them.
 *
 * Re-fetches on every quarter_advanced WS frame via query invalidation
 * done in TeamView.
 */
export default function TrajectoryPanel({
  sessionId, teamId, catalog,
}: { sessionId: string; teamId: string; catalog: ActivityCatalogEntry[] }) {
  const historyQ = useQuery({
    queryKey: ['session-history', sessionId],
    queryFn: () => api.getSessionHistory(sessionId),
    refetchInterval: 15_000,
  })

  if (historyQ.isLoading || !historyQ.data) {
    return (
      <div className="card">
        <h2 className="text-base font-semibold mb-2">Your trajectory</h2>
        <p className="text-sm text-slate-500 italic">Loading…</p>
      </div>
    )
  }

  const h = historyQ.data
  const team = h.teams[teamId]
  if (!team || team.snapshots.length === 0) {
    return (
      <div className="card">
        <h2 className="text-base font-semibold mb-2">Your trajectory</h2>
        <p className="text-sm text-slate-500 italic">
          Charts will appear after the first quarter is advanced.
        </p>
      </div>
    )
  }

  const visibleKPIs = KPI_DEFS.filter(k => k.visibleToTeam)

  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-base font-semibold mb-3">
          Your trajectory <span className="text-sm font-normal text-slate-500">· through Q{h.current_quarter}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {visibleKPIs.map(kpi => (
            <KPIChart
              key={kpi.key}
              kpi={kpi}
              teams={[team]}
              teamOrder={h.team_order}
              currentQuarter={h.current_quarter}
              height={160}
            />
          ))}
        </div>
      </div>
      <DecisionHistoryList snapshots={team.snapshots} catalog={catalog} />
    </div>
  )
}
